import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GameState } from '../types';
import { MockAIService } from '../services/AIService';
import { RealVoiceService } from '../services/VoiceService';

interface VoiceChatProps {
    gameContext: GameState;
    onResponse: (response: string, suggestedWord?: string, userGender?: 'boy' | 'girl', userInput?: string) => void;
    aiService: MockAIService;
    voiceService: RealVoiceService;
}

export const VoiceChat: React.FC<VoiceChatProps> = ({ 
    gameContext, 
    onResponse, 
    aiService, 
    voiceService 
}) => {
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [lastResponse, setLastResponse] = useState('');
    const [textInput, setTextInput] = useState('');
    const [showTextInput, setShowTextInput] = useState(false);
    const [conversationStarted, setConversationStarted] = useState(false);
    const [voiceRetryCount, setVoiceRetryCount] = useState(0);
    const [lastVoiceCallTime, setLastVoiceCallTime] = useState(0);
    const [isVoiceInProgress, setIsVoiceInProgress] = useState(false);
    const [lastStoppedStage, setLastStoppedStage] = useState('');
    const [isProcessingResult, setIsProcessingResult] = useState(false);
    const [userUsedNonVoiceInput, setUserUsedNonVoiceInput] = useState(false);
    const [autoStartTimeout, setAutoStartTimeout] = useState<NodeJS.Timeout | null>(null);
    const userUsedNonVoiceInputRef = useRef(false);

    const startConversation = useCallback(async () => {
        if (conversationStarted) return;
        
        setConversationStarted(true);
        setIsListening(false);
        setIsSpeaking(true);

        try {
            const aiResponse = await aiService.chatWithChild('', gameContext, voiceService);
            setLastResponse(aiResponse);

            // Use audio-first methods instead of TTS
            let audioPlayed = false;
            
            if (gameContext.stage === 'ask_word') {
                audioPlayed = await aiService.playMessage('ask_word', voiceService);
            } else if (gameContext.stage === 'greeting_and_ask_name') {
                audioPlayed = await aiService.playMessage('greeting_and_ask_name', voiceService);
            } else if (gameContext.stage === 'lets_start' && gameContext.childName) {
                // Play the lets_start audio
                audioPlayed = await aiService.playLetsStart(gameContext.childName, voiceService);
                
                // Auto-transition immediately after audio finishes
                setIsSpeaking(false);
                console.log('🚀 lets_start audio finished in startConversation, auto-transitioning to ask_word');
                onResponse(aiResponse, undefined, undefined, 'auto-transition');
                return; // Exit early, don't start listening
            } else if (gameContext.stage === 'ask_gender') {
                // Legacy support
                audioPlayed = await aiService.playMessage('ask_gender', voiceService);
            } else if (gameContext.stage === 'ask_name') {
                // Legacy support
                audioPlayed = await aiService.playMessage('ask_name', voiceService);
            } else if (gameContext.stage === 'ask_ready' && gameContext.childName) {
                // Legacy support
                audioPlayed = await aiService.playGreeting(gameContext.childName, voiceService);
            }
            
            // Only use TTS if no audio method was called or if it failed (but not for lets_start)
            if (!audioPlayed && gameContext.stage !== 'ask_word' && gameContext.stage !== 'greeting_and_ask_name' && 
                gameContext.stage !== 'lets_start' && gameContext.stage !== 'ask_gender' && 
                gameContext.stage !== 'ask_name' && gameContext.stage !== 'ask_ready') {
                await voiceService.speak(aiResponse);
            }
            
            setIsSpeaking(false);

            // Only start listening for stages that need user input (but not on iOS Chrome)
            if (gameContext.stage !== 'lets_start') {
                if (voiceService.isSpeechRecognitionAvailable()) {
                    setTimeout(() => {
                        handleVoiceInput();
                    }, 200);
                } else {
                    console.log('📱 Skipping auto voice input on unsupported device');
                    setShowTextInput(true);
                }
            }

        } catch (error) {
            console.error('Conversation error:', error);
            setIsSpeaking(false);
            setShowTextInput(true);
        }
    }, [aiService, gameContext, voiceService, conversationStarted, onResponse]);

    useEffect(() => {
        console.log(`🔍 VoiceChat useEffect triggered with stage: ${gameContext.stage}`);
        if (gameContext.stage !== 'typing' && gameContext.stage !== 'celebration' && gameContext.stage !== 'start_screen') {
            console.log(`✅ Entering conversation logic for stage: ${gameContext.stage}`);
            // Reset the stopped stage flag when we leave typing/celebration
            if (lastStoppedStage && (lastStoppedStage === 'typing' || lastStoppedStage === 'celebration')) {
                setLastStoppedStage('');
                // Also reset conversation state to allow fresh start
                setConversationStarted(false);
            }
            
            // IMPORTANT: Reset conversation for lets_start stage to ensure it triggers
            if (gameContext.stage === 'lets_start') {
                console.log('🔄 Resetting conversation for lets_start stage');
                setConversationStarted(false);
            }
            
            // Only restart conversation if we haven't already started one for this stage
            if (!conversationStarted) {
                const timer = setTimeout(() => {
                setConversationStarted(true);
                (async () => {
                    try {
                        setIsSpeaking(true);
                        const aiResponse = await aiService.chatWithChild('', gameContext);
                        setLastResponse(aiResponse);
                        
                        // Use audio-first methods instead of direct TTS
                        let audioPlayed = false;
                        
                        if (gameContext.stage === 'ask_word') {
                            audioPlayed = await aiService.playMessage('ask_word', voiceService);
                        } else if (gameContext.stage === 'greeting_and_ask_name') {
                            audioPlayed = await aiService.playMessage('greeting_and_ask_name', voiceService);
                        } else if (gameContext.stage === 'lets_start' && gameContext.childName) {
                            // Play the lets_start audio
                            audioPlayed = await aiService.playLetsStart(gameContext.childName, voiceService);
                            
                            // Auto-transition immediately after audio finishes
                            setIsSpeaking(false);
                            console.log('🚀 lets_start audio finished, auto-transitioning to ask_word');
                            console.log('📞 Calling onResponse for lets_start auto-transition');
                            onResponse(aiResponse, undefined, undefined, 'auto-transition');
                            return; // Exit early, don't start listening
                        } else if (gameContext.stage === 'ask_gender') {
                            // Legacy support
                            audioPlayed = await aiService.playMessage('ask_gender', voiceService);
                        } else if (gameContext.stage === 'ask_name') {
                            // Legacy support
                            audioPlayed = await aiService.playMessage('ask_name', voiceService);
                        } else if (gameContext.stage === 'ask_ready' && gameContext.childName) {
                            // Legacy support
                            audioPlayed = await aiService.playGreeting(gameContext.childName, voiceService);
                        }
                        
                        // Only use TTS if no audio method was available
                        if (!audioPlayed && gameContext.stage !== 'ask_word' && gameContext.stage !== 'greeting_and_ask_name' && 
                            gameContext.stage !== 'lets_start' && gameContext.stage !== 'ask_gender' && 
                            gameContext.stage !== 'ask_name' && gameContext.stage !== 'ask_ready') {
                            await voiceService.speak(aiResponse);
                        }
                        
                        setIsSpeaking(false);
                        // Auto-start voice input for ask_word and greeting_and_ask_name stages
                        
                        if (gameContext.stage === 'ask_word') {
                            console.log(`🔍 Checking auto-start conditions: userUsedNonVoiceInput=${userUsedNonVoiceInput}, ref=${userUsedNonVoiceInputRef.current}`);
                            if (userUsedNonVoiceInput || userUsedNonVoiceInputRef.current) {
                                console.log('🚫 Skipping auto voice input - user used non-voice input');
                            } else if (voiceService.isSpeechRecognitionAvailable()) {
                                console.log('🎤 Auto-starting voice input for word selection');
                                const timeoutId = setTimeout(() => {
                                    if (gameContext.stage === 'ask_word' && !userUsedNonVoiceInput && !userUsedNonVoiceInputRef.current) {
                                        handleVoiceInput();
                                    } else if (userUsedNonVoiceInput || userUsedNonVoiceInputRef.current) {
                                        console.log('🚫 Cancelled scheduled voice input - user used non-voice input');
                                    }
                                    setAutoStartTimeout(null);
                                }, 800);
                                setAutoStartTimeout(timeoutId);
                            } else {
                                console.log('📱 Skipping auto voice input on unsupported device');
                                setShowTextInput(true);
                            }
                        } else if (gameContext.stage === 'greeting_and_ask_name') {
                            if (voiceService.isSpeechRecognitionAvailable()) {
                                console.log('🎤 Auto-starting voice input for name question');
                                setTimeout(() => {
                                    if (gameContext.stage === 'greeting_and_ask_name') {
                                        handleVoiceInput();
                                    }
                                }, 800);
                            } else {
                                console.log('📱 Skipping auto voice input on unsupported device');
                                setShowTextInput(true);
                            }
                        } else {
                            setShowTextInput(true);
                        }
                    } catch (error) {
                        console.error('Conversation error:', error);
                        setIsSpeaking(false);
                        setShowTextInput(true);
                    }
                })();
                }, 200);
                return () => clearTimeout(timer);
            }
        } else {
            console.log(`🚫 Entering stop logic for stage: ${gameContext.stage}`);
            // If we're in typing or celebration stage, stop any listening aggressively
            if (gameContext.stage === 'typing' || gameContext.stage === 'celebration') {
                // Only run the stopping logic if we haven't already stopped for this stage
                if (lastStoppedStage !== gameContext.stage) {
                    console.log('🛑 Aggressively stopping voice listening - in typing/celebration stage');
                    setIsListening(false);
                    setIsSpeaking(false);
                    setIsVoiceInProgress(false);
                    setConversationStarted(false); // Prevent restart
                    setShowTextInput(false); // Hide text input during typing
                    voiceService.stopListening();
                    voiceService.stopSpeaking();
                    
                    // Cancel any scheduled auto-start timeout
                    if (autoStartTimeout) {
                        clearTimeout(autoStartTimeout);
                        setAutoStartTimeout(null);
                    }
                    
                    // Reset non-voice input flag for next conversation
                    setUserUsedNonVoiceInput(false);
                    userUsedNonVoiceInputRef.current = false;
                    
                    // Mark this stage as stopped to prevent repeated execution
                    setLastStoppedStage(gameContext.stage);
                }
                // Remove the else block to prevent excessive logging
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameContext.stage, gameContext.childName, conversationStarted, lastStoppedStage, userUsedNonVoiceInput]);

    const handleUserInput = async (input: string) => {
        try {
            console.log(`🎯 handleUserInput called with: "${input}" in stage: ${gameContext.stage}`);
            
            // Don't process empty or whitespace-only inputs
            if (!input || input.trim().length === 0) {
                console.log(`❌ Ignoring empty input - not processing`);
                return;
            }
            
            console.log(`🔇 Stopping any active speech...`);
            voiceService.stopSpeaking();
            setIsSpeaking(false);

            console.log(`🤖 Getting AI response for input: "${input}"`);
            const aiResponse = await aiService.chatWithChild(input, gameContext);
            console.log(`🤖 AI response received: "${aiResponse}"`);
            setLastResponse(aiResponse);

            let suggestedWord: string | undefined = undefined;
            let userGender: 'boy' | 'girl' | undefined = undefined;

            // Handle different stage inputs
            if (gameContext.stage === 'ask_gender') {
                // Legacy support
                userGender = input.includes('בת') ? 'girl' : 'boy';
            } else if (gameContext.stage === 'ask_word') {
                console.log(`🎯 Processing ask_word input: "${input}"`);
                try {
                    suggestedWord = aiService.extractWordFromResponse(input);
                    console.log(`🎯 Extracted word: "${suggestedWord}"`);
                    console.log(`🎯 Will transition to typing: ${!!suggestedWord}`);
                } catch (extractError) {
                    console.error(`❌ Error extracting word: ${extractError}`);
                    suggestedWord = input.trim(); // Use the input directly as fallback
                    console.log(`🔄 Using fallback word: "${suggestedWord}"`);
                }
            }
            // Note: greeting_and_ask_name and lets_start don't need special input processing

            setIsListening(false);
            setShowTextInput(false);
            setTextInput('');
            
            console.log(`🚀 Calling onResponse with word: "${suggestedWord}"`);
            console.log(`📞 onResponse parameters:`, { aiResponse, suggestedWord, userGender, input });
            onResponse(aiResponse, suggestedWord, userGender, input);
            console.log(`✅ onResponse call completed`);
            
            setIsListening(false);
            setShowTextInput(false);
            setTextInput('');

            // Don't continue listening after word selection - let the typing stage handle it
            if (gameContext.stage === 'ask_word' && suggestedWord) {
                console.log('🎯 Word selected, stopping voice chat to transition to typing');
                return; // Exit early, don't start listening again
            }

            if (gameContext.stage === 'ask_word' && !suggestedWord) {
                setIsSpeaking(true);
                await voiceService.speak(aiResponse);
                setIsSpeaking(false);

                // TEMPORARILY: Don't restart voice input automatically to prevent loops
                console.log('🛑 Not auto-restarting voice input - user must click microphone button');
                setShowTextInput(true);
            }

        } catch (error) {
            console.error('Chat error:', error);
            setIsListening(false);
            setIsSpeaking(false);
            setShowTextInput(true);
        }
    };

    const handleVoiceInput = async () => {
        console.log('🎯 handleVoiceInput called');
        console.log('Current state:', { isListening, isSpeaking, isVoiceInProgress, conversationStarted });
        
        try {
            const now = Date.now();
            
            // Check if voice is already in progress
            if (isVoiceInProgress) {
                console.log('🚫 Voice input already in progress - ignoring duplicate call');
                return;
            }
            
            // Debounce - prevent rapid consecutive calls (within 500ms)  
            if (now - lastVoiceCallTime < 500) {
                console.log('🚫 Voice input debounced - ignoring rapid consecutive call');
                return;
            }
            setLastVoiceCallTime(now);
            setIsVoiceInProgress(true);
            
            // Mark user gesture for mobile devices
            (voiceService as any).markUserGesture?.();
            
            setIsListening(true);
            setShowTextInput(false);
            const userSpeech = await voiceService.listen();
            
            // Reset retry count on successful recognition
            setVoiceRetryCount(0);
            setIsVoiceInProgress(false);
            console.log(`🎤 Processing voice input: "${userSpeech}"`);
            
            setIsProcessingResult(true);
            try {
                await handleUserInput(userSpeech);
                console.log(`✅ Voice input processed successfully`);
                
                // IMPORTANT: Immediately stop voice recognition after successful processing
                // This prevents the same session from continuing to listen (especially on iOS)
                console.log('🛑 Force-stopping voice recognition after successful processing');
                setIsListening(false);
                setIsVoiceInProgress(false);
                voiceService.stopListening();
            } catch (userInputError) {
                console.error(`❌ Error processing voice input: ${userInputError}`);
            } finally {
                setIsProcessingResult(false);
            }
        } catch (error) {
            setIsListening(false);
            setIsVoiceInProgress(false);
            console.error('Voice input error:', error);
            
            // Check for "Already listening" error - don't retry in this case
            if (error instanceof Error && error.message.includes('Already listening')) {
                console.log('⚠️ Voice service already active - ignoring duplicate request');
                return; // Don't show text input, just ignore the duplicate call
            }
            
            // TEMPORARILY: No retries to avoid infinite loop during debugging
            console.log('🛑 Stopping voice input, showing text input instead');
            setVoiceRetryCount(0);
            setShowTextInput(true);
        }
    };

    const handleTextSubmit = async () => {
        if (textInput.trim()) {
            // IMPORTANT: Stop voice recognition but preserve TTS when user submits text input
            console.log('🛑 User submitted text input - stopping voice recognition (preserving TTS)');
            setIsListening(false);
            setIsVoiceInProgress(false);
            voiceService.stopListening();
            // Note: NOT calling stopSpeaking() to preserve word introduction TTS
            
            // Cancel any scheduled auto-start timeout
            if (autoStartTimeout) {
                console.log('🚫 Cancelling scheduled auto-start timeout');
                clearTimeout(autoStartTimeout);
                setAutoStartTimeout(null);
            }
            
            // Mark that user used non-voice input to prevent auto-restart
            setUserUsedNonVoiceInput(true);
            userUsedNonVoiceInputRef.current = true;
            
            // Mark user gesture for mobile TTS
            if (voiceService.markUserGesture) {
                voiceService.markUserGesture();
            }
            await handleUserInput(textInput);
            setTextInput('');
        }
    };

    const handleQuickResponse = async (response: string) => {
        // Mark user gesture for mobile TTS and test TTS immediately
        if (voiceService.markUserGesture) {
            voiceService.markUserGesture();
        }
        
        // IMPORTANT: Stop voice recognition but preserve TTS when user clicks suggested word
        console.log('🛑 User clicked suggested word - stopping voice recognition (preserving TTS)');
        setIsListening(false);
        setIsVoiceInProgress(false);
        voiceService.stopListening();
        // Note: NOT calling stopSpeaking() to preserve word introduction TTS
        
        // Cancel any scheduled auto-start timeout
        if (autoStartTimeout) {
            console.log('🚫 Cancelling scheduled auto-start timeout');
            clearTimeout(autoStartTimeout);
            setAutoStartTimeout(null);
        }
        
        // Mark that user used non-voice input to prevent auto-restart
        setUserUsedNonVoiceInput(true);
        userUsedNonVoiceInputRef.current = true;
        
        // Skip TTS test on tablets since it doesn't work and causes delays
        console.log('📱 Skipping TTS test on mobile/tablet to prevent delays');
        
        setShowTextInput(false);
        await handleUserInput(response);
    };

    // Reset function to clear stuck states
    const resetVoiceState = () => {
        if (isProcessingResult) {
            console.log('🚫 Reset blocked - processing voice result');
            return;
        }
        
        console.log('🔄 Resetting voice state');
        setIsListening(false);
        setIsSpeaking(false);
        setIsVoiceInProgress(false);
        setConversationStarted(false);
        setLastStoppedStage('');
        voiceService.stopListening();
        voiceService.stopSpeaking();
    };

    // Auto-reset if stuck for too long
    useEffect(() => {
        if (isVoiceInProgress) {
            const timeout = setTimeout(() => {
                console.log('⚠️ Voice state stuck, auto-resetting...');
                resetVoiceState();
            }, 20000); // 20 second timeout
            return () => clearTimeout(timeout);
        }
    }, [isVoiceInProgress]);

    const getQuickResponses = () => {
        if (gameContext.isSimpleMode) {
            if (gameContext.stage === 'ask_word') return ['חתול', 'כלב', 'פרח', 'דג'];
        } else {
            switch (gameContext.stage) {
                // New streamlined flow
                case 'greeting_and_ask_name': return ['בן', 'רובי', 'תום', 'אביב', 'נועה', 'שירה'];
                case 'lets_start': return ['כן', 'בטח', 'בואו נתחיל'];
                case 'ask_word': return ['חתול', 'כלב', 'פרח', 'דג'];
                // Legacy support
                case 'ask_gender': return ['אני בן', 'אני בת'];
                case 'ask_name': return ['בן', 'רובי', 'תום', 'אביב'];
                case 'ask_ready': return ['כן', 'בטח'];
                default: return [];
            }
        }
        return [];
    };

    return (
        <>
            <style>
                {`
                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.1); }
                        100% { transform: scale(1); }
                    }
                `}
            </style>
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                marginBottom: '24px'
            }}>

            {/* Only show the microphone icon when listening */}
            {isListening && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{
                        display: 'inline-block',
                        padding: '20px',
                        borderRadius: '50%',
                        transition: 'all 0.3s',
                        backgroundColor: '#fef3c7',
                        border: '6px solid #f59e0b',
                        animation: 'pulse 1.5s infinite',
                        boxShadow: '0 0 20px rgba(245, 158, 11, 0.5)'
                    }}>
                        <div style={{ fontSize: '48px' }}>
                            🎤
                        </div>
                    </div>
                </div>
            )}

            {/* Alternate between teacher speaking and listening panels */}
            {isListening && (
                <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    border: '3px solid #f59e0b',
                    animation: 'pulse 1.5s infinite'
                }}>
                    <div style={{ fontWeight: 'bold', color: '#92400e', textAlign: 'center', direction: 'rtl', fontSize: '18px' }}>
                        🎤 המורה מקשיב עכשיו!
                    </div>
                    <div style={{
                        color: '#92400e',
                        fontSize: '16px',
                        textAlign: 'center',
                        marginTop: '8px',
                        direction: 'rtl'
                    }}>
                        דבר בקול ברור ואז חכה שהמורה יענה
                    </div>
                </div>
            )}

            {/* Show teacher's response when not listening */}
            {lastResponse && !isListening && (
                <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '8px',
                    border: '2px solid #3b82f6'
                }}>
                    <div style={{ fontWeight: 'bold', color: '#1d4ed8', textAlign: 'center', direction: 'rtl' }}>המורה אומר</div>
                    <div style={{
                        color: '#1e40af',
                        fontSize: '18px',
                        textAlign: 'right',
                        marginTop: '8px',
                        direction: 'rtl'
                    }}>
                        {lastResponse}
                    </div>
                </div>
            )}

            {/* Word selection and text input functionality - always visible */}
            <div style={{ marginTop: '16px' }}>
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    justifyContent: 'center',
                    marginBottom: '16px'
                }}>
                    {getQuickResponses().map((response) => (
                        <button
                            key={response}
                            onClick={() => handleQuickResponse(response)}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            {response}
                        </button>
                    ))}
                </div>

                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                    <input
                        type="text"
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleTextSubmit()}
                        placeholder="או כתב תשובה אחרת"
                        style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '16px',
                            textAlign: 'right',
                            direction: 'rtl'
                        }}
                    />
                    <button
                        onClick={handleTextSubmit}
                        disabled={!textInput.trim()}
                        style={{
                            width: '100%',
                            marginTop: '8px',
                            padding: '8px 16px',
                            backgroundColor: textInput.trim() ? '#10b981' : '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 'bold',
                            cursor: textInput.trim() ? 'pointer' : 'not-allowed'
                        }}
                    >
                        📝 שלח תשובה
                    </button>
                </div>
            </div>

            </div>
        </>
    );
};